import type {
	ShortcutBindingsMap,
	ShortcutConflictEntry,
	ShortcutConflictSeverity,
	ShortcutContext,
} from 'src/stores/shortcuts/types';

import { SHORTCUT_ACTIONS_BY_ID } from 'src/util/shortcuts/actionsRegistry';

const CATEGORY_PRIORITY: Readonly<Record<string, number>> = {
	detail: 3,
	global: 0,
	list: 2,
	navigation: 1,
};

interface ActionShape {
	category: string;
	contexts: ShortcutContext[];
}

function shapeForAction(actionId: string): ActionShape {
	const action = SHORTCUT_ACTIONS_BY_ID[actionId];

	if (action === undefined) {
		// Unbekannte IDs konservativ wie global behandeln, damit echte Kollisionen nie verschluckt werden.
		return { category: 'global', contexts: ['global'] };
	}

	return { category: action.category, contexts: action.contexts };
}

function contextsIntersect(left: ShortcutContext[], right: ShortcutContext[]): boolean {
	return left.some((context) => right.includes(context));
}

function isSubsetOf(candidate: ShortcutContext[], container: ShortcutContext[]): boolean {
	return candidate.every((context) => container.includes(context));
}

/**
 * Kontext-sensitive Schwere einer Tastenkollision zweier Actions:
 * - gleiche Kategorie + überlappende Kontexte → 'conflict'
 * - gleiche Kategorie + disjunkte Kontexte → kein Problem (z. B. detail:task vs. detail:note)
 * - verschiedene Kategorien: der Kategorie-Verlierer wird deterministisch überdeckt.
 *   Ist er dabei überall überdeckt (Kontext-Teilmenge des Gewinners), ist er unerreichbar → 'conflict',
 *   sonst nur situativ überdeckt → 'shadowed'.
 */
export function severityForBindingPair(leftActionId: string, rightActionId: string): ShortcutConflictSeverity | null {
	const left = shapeForAction(leftActionId);
	const right = shapeForAction(rightActionId);

	if (left.category === right.category) {
		return contextsIntersect(left.contexts, right.contexts) ? 'conflict' : null;
	}

	const leftPriority = CATEGORY_PRIORITY[left.category] ?? 0;
	const rightPriority = CATEGORY_PRIORITY[right.category] ?? 0;
	const winner = leftPriority >= rightPriority ? left : right;
	const loser = leftPriority >= rightPriority ? right : left;

	if (isSubsetOf(loser.contexts, winner.contexts)) {
		return 'conflict';
	}

	return 'shadowed';
}

export function findShortcutConflicts(bindings: ShortcutBindingsMap): ShortcutConflictEntry[] {
	const keysIndex = new Map<string, string[]>();

	for (const binding of Object.values(bindings)) {
		if (!binding.enabled || binding.keys.length === 0) {
			continue;
		}

		const existing = keysIndex.get(binding.keys) ?? [];
		existing.push(binding.id);
		keysIndex.set(binding.keys, existing);
	}

	const conflicts: ShortcutConflictEntry[] = [];

	for (const [keys, bindingIds] of keysIndex.entries()) {
		if (bindingIds.length < 2) {
			continue;
		}

		let worstSeverity: ShortcutConflictSeverity | null = null;
		const affectedIds = new Set<string>();

		for (let leftIndex = 0; leftIndex < bindingIds.length; leftIndex += 1) {
			for (let rightIndex = leftIndex + 1; rightIndex < bindingIds.length; rightIndex += 1) {
				const leftId = bindingIds[leftIndex] ?? '';
				const rightId = bindingIds[rightIndex] ?? '';
				const severity = severityForBindingPair(leftId, rightId);

				if (severity === null) {
					continue;
				}

				affectedIds.add(leftId);
				affectedIds.add(rightId);

				if (severity === 'conflict' || worstSeverity === null) {
					worstSeverity = severity;
				}
			}
		}

		if (worstSeverity !== null) {
			conflicts.push({ bindingIds: [...affectedIds], keys, severity: worstSeverity });
		}
	}

	return conflicts;
}

const RESERVED_KEY_COMBOS: ReadonlySet<string> = new Set([
	'Mod+T',
	'Mod+W',
	'Mod+N',
	'Mod+Q',
	'Mod+R',
	'Mod+L',
	'Mod+Tab',
	'Meta+T',
	'Meta+W',
	'Meta+N',
	'Meta+Q',
	'Meta+R',
	'Meta+L',
	'Meta+Tab',
	'Ctrl+T',
	'Ctrl+W',
	'Ctrl+N',
	'Ctrl+R',
	'Ctrl+L',
	'F5',
	'F11',
]);

export function isKeysReserved(keys: string): boolean {
	if (keys.length === 0) {
		return false;
	}

	return keys.split(' ').some((segment) => RESERVED_KEY_COMBOS.has(segment));
}
